import {
	faColumns,
	faEye,
	faFilter,
	faFire,
	faPencil,
	faSearch,
	faSort,
	faTableColumns,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	FilterRulesService,
	ListOverridesQueryService,
	preserveDynamicDates,
	SavedSearchQueryService,
	type FilterRule,
	type ListOverrideEditorViewModel,
	type SavedSearchModel,
} from '@supply-mover-2-0/core';
import {
	PermissionService,
	type ListOverrideSummaryDto,
	type SaveListOverrideDto,
} from '@supply-mover-2-0/shared';
import clsx from 'clsx';
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
	type ReactElement,
} from 'react';
import { Tabs } from '../../../components/navigation/Tabs';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { Toast } from '../../../services/ToastService';
import { tailwindClasses } from '../../../themes/tailwind-classes';
import type {
	ColumnConfiguration,
	ListBuilderConfiguration,
	ListConfiguration,
} from '../../types';
import {
	FiltersTabContent,
	type FiltersTabHandle,
	type FiltersTabPendingChanges,
} from '../filters-editor/FiltersTabContent';
import { ColumnConfigTabs } from '../list-configuration/columns/ColumnConfigTabs';
import { OverridePreview } from '../overrides/OverridePreview';
import {
	EVERYONE_SLOT,
	OverrideSlotPicker,
	slotKey,
	slotLabel,
	type OverrideSlot,
} from '../overrides/OverrideSlotPicker';
import { TabStripEditor } from '../overrides/TabStripEditor';
import { SaveNewListModal } from '../saved-search/SaveNewListModal';
import {
	SortTabContent,
	type SortTabHandle,
	type SortTabPendingChanges,
} from '../sort-editor/SortTabContent';

/**
 * Merge the Sort tab's curated `sortOptions` and the Filters tab's overrides
 * into a single listOverrides blob for the saved-search update. Both tabs seed
 * from the same stored listOverrides and touch disjoint keys (filters →
 * showFiltersByDefault, sort → sortOptions), so we start from the filter tab's
 * full view (or the existing stored value when filters weren't touched) and
 * overlay the sort tab's decision. Returns null when the result is empty.
 */
function mergeListOverrides(
	existing: Record<string, unknown> | null,
	filterChanges: FiltersTabPendingChanges | null,
	sortChanges: SortTabPendingChanges | null,
): Record<string, unknown> | null {
	let base: Record<string, unknown> | null =
		filterChanges != null ? filterChanges.listOverrides : existing;

	if (sortChanges != null) {
		const next: Record<string, unknown> = { ...(base ?? {}) };
		if (sortChanges.sortOptions == null) {
			delete next['sortOptions'];
		} else {
			next['sortOptions'] = sortChanges.sortOptions;
		}
		base = Object.keys(next).length > 0 ? next : null;
	}

	return base;
}

/**
 * Per-filter, per-field difference between the rules a slot started from
 * and the rules as edited (PD-14278). An override stores only what changed
 * about a rule, so a field the admin never touched keeps following the
 * layer beneath.
 */
function diffFilterRules(
	base: Record<string, FilterRule>,
	working: Record<string, FilterRule>,
): Record<string, Partial<FilterRule>> | null {
	const changes: Record<string, Partial<FilterRule>> = {};
	for (const [filterId, rule] of Object.entries(working)) {
		const before = (base[filterId] ?? {}) as Record<string, unknown>;
		const after = rule as unknown as Record<string, unknown>;
		const partial: Record<string, unknown> = {};
		for (const key of new Set([
			...Object.keys(before),
			...Object.keys(after),
		])) {
			if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
				partial[key] = after[key] ?? null;
			}
		}
		if (Object.keys(partial).length > 0) {
			changes[filterId] = partial as Partial<FilterRule>;
		}
	}
	return Object.keys(changes).length > 0 ? changes : null;
}

/** The misc knobs a resolved list carries that the Filters / Sort tabs edit. */
function listOverridesOf(list: ListConfiguration): Record<string, unknown> {
	const out: Record<string, unknown> = {};
	if (list.showFiltersByDefault != null) {
		out['showFiltersByDefault'] = list.showFiltersByDefault;
	}
	if (list.sortOptions != null) {
		out['sortOptions'] = list.sortOptions;
	}
	if (list.rowExpansion != null) {
		out['rowExpansion'] = list.rowExpansion;
	}
	return out;
}

const TAB_STRIP_TOOL = '__tab-strip';
const PREVIEW_TOOL = '__preview';

// ── Types ────────────────────────────────────────────

interface SidebarItem {
	id: number | string;
	name: string;
	type: 'default' | 'mine' | 'shared' | 'tool';
	canEdit: boolean;
	totalUseCount: number;
	savedSearch: SavedSearchModel | null;
	defaultListConfig: ListConfiguration | null;
}

export interface ColumnEditorHandle {
	saveAll: () => Promise<boolean>;
	openCopyList: () => void;
}

export interface ColumnEditorState {
	isDirty: boolean;
	canSaveDirect: boolean;
	hasPendingChanges: boolean;
	/**
	 * The saved search currently selected in the sidebar, or null when the
	 * selection is a bundled (read-only) list. The modal footer uses this to
	 * offer "Set as Default for Everyone", which needs the copy's id, its name
	 * for the tab configuration, and its visibility to know whether it must be
	 * shared before it can resolve for other users (PD-13666).
	 */
	selectedSavedSearch: SavedSearchModel | null;
}

interface PendingChange {
	columns: Array<ColumnConfiguration>;
	defaultSortId: string | undefined;
}

interface ColumnEditorLayoutProps {
	listKey: string;
	editorLists: Array<ListConfiguration>;
	configuration: ListBuilderConfiguration | undefined;
	activeListId: string | undefined;
	availableFields: Array<unknown>;
	isModalOpen: boolean;
	onUpdateList: (listId: string, updates: Partial<ListConfiguration>) => void;
	onStateChange?: (state: ColumnEditorState) => void;
	onColumnsUpdated?: (
		columns: Array<ColumnConfiguration>,
		defaultSortId?: string,
	) => void;
	/**
	 * Called after a saved search is successfully persisted, with the updated
	 * model. Lets the host re-apply the saved search to the live view so filter /
	 * activeFilter / label-override edits show immediately without a page refresh.
	 */
	onSavedSearchSaved?: (savedSearch: SavedSearchModel) => void;
	/**
	 * PD-14278 — called after an override or tab strip is saved so the host
	 * re-resolves the bundled lists and re-applies the active one.
	 */
	onOverridesSaved?: () => void;
	initialSelectedSearchId?: string | number;
	activeFilterState?: Record<string, unknown>;
}

// ── Helpers ──────────────────────────────────────────

const TYPE_BADGE_STYLES = {
	default: 'bg-subtle text-muted',
	mine: tailwindClasses.badgeInfo,
	shared: tailwindClasses.badgeSuccess,
	tool: 'bg-subtle text-muted',
};

const TYPE_LABELS: Record<string, string> = {
	default: 'Standard',
	mine: 'Mine',
	shared: 'Shared',
	tool: '',
};

function formatUseCount(count: number): string {
	if (count < 1000) {
		return String(count);
	}
	return `${Math.floor(count / 1000)}k+`;
}

// ── Component ────────────────────────────────────────

export const ColumnEditorLayout = forwardRef<
	ColumnEditorHandle,
	ColumnEditorLayoutProps
>(function ColumnEditorLayout(
	{
		listKey,
		editorLists,
		configuration,
		activeListId,
		availableFields,
		isModalOpen,
		onUpdateList,
		onStateChange,
		onColumnsUpdated,
		onSavedSearchSaved,
		onOverridesSaved,
		initialSelectedSearchId,
		activeFilterState,
	},
	ref,
) {
	const colors = useThemeColors();
	const { t } = useTranslation();

	// ── State ────────────────────────────────────────
	const [savedSearches, setSavedSearches] = useState<Array<SavedSearchModel>>(
		[],
	);
	const [isLoading, setIsLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedItemId, setSelectedItemId] = useState<string | number | null>(
		null,
	);
	const [workingColumns, setWorkingColumns] = useState<
		Array<ColumnConfiguration>
	>([]);
	const [workingDefaultSortId, setWorkingDefaultSortId] = useState<
		string | undefined
	>(undefined);
	const [isDirty, setIsDirty] = useState(false);
	const [showSaveAsModal, setShowSaveAsModal] = useState(false);
	// listOverrides (showFiltersByDefault + curated sortOptions) captured when
	// "Copy List" is clicked, so the copy inherits the source's curation —
	// including any unsaved Sort/Filters-tab edits in progress.
	const [copyListOverrides, setCopyListOverrides] = useState<
		Record<string, unknown> | undefined
	>(undefined);
	const [showEditModal, setShowEditModal] = useState(false);
	const [editingSavedSearch, setEditingSavedSearch] =
		useState<SavedSearchModel | null>(null);
	const [pendingChanges, setPendingChanges] = useState<
		Map<string | number, PendingChange>
	>(new Map());

	// Right-pane tab switcher: Columns (existing) vs Filters vs Sort.
	const [activeTab, setActiveTab] = useState<'columns' | 'filters' | 'sort'>(
		'columns',
	);

	// Filters tab tracks its own dirty state and exposes a getPendingChanges()
	// imperative handle that handleSaveAll consults to bundle filter edits
	// into the same SavedSearchQueryService.update call as column edits.
	const filtersTabRef = useRef<FiltersTabHandle>(null);
	const [filtersTabDirty, setFiltersTabDirty] = useState(false);

	// Sort tab — same pattern as the Filters tab. Its curated sort options are
	// folded into the saved search's listOverrides; the default it picks flows
	// through workingDefaultSortId (persisted as sortId).
	const sortTabRef = useRef<SortTabHandle>(null);
	const [sortTabDirty, setSortTabDirty] = useState(false);

	const originalColumnsRef = useRef<Array<ColumnConfiguration>>([]);

	// ── Overrides (PD-14278) ────────────────────────
	// A standard list is edited in place, for a SLOT: which lists the change
	// covers (this one / every list of the entity) and who it is for (everyone
	// / a role). The editor view is the list as it stands beneath and at that
	// slot; the delta is derived server-side when saving.
	const [overrideSlot, setOverrideSlot] = useState<OverrideSlot>(EVERYONE_SLOT);
	const [editorView, setEditorView] =
		useState<ListOverrideEditorViewModel | null>(null);
	const [isLoadingView, setIsLoadingView] = useState(false);
	const [overrides, setOverrides] = useState<Array<ListOverrideSummaryDto>>([]);
	const [roles, setRoles] = useState<Array<{ id: number; name: string }>>([]);
	const entityLabel = useMemo(() => {
		const raw = configuration?.objectType ?? listKey;
		const words = raw
			.replace(/[-_]+/g, ' ')
			.replace(/([a-z])([A-Z])/g, '$1 $2')
			.trim()
			.replace(/\b\w/g, (letter) => letter.toUpperCase());
		return words.endsWith('s') ? words : `${words}s`;
	}, [configuration?.objectType, listKey]);

	const fetchOverrides = useCallback(async () => {
		const [summaries, ladder] = await Promise.all([
			ListOverridesQueryService.list(listKey),
			ListOverridesQueryService.roleLadder(),
		]);
		if (summaries.success) {
			setOverrides(summaries.data);
		}
		if (ladder.success && ladder.data != null) {
			setRoles(ladder.data.roles);
		}
	}, [listKey]);

	// Slots that already hold an override, keyed for the picker's marks.
	const existingSlotKeys = useMemo(() => {
		const keys = new Set<string>();
		for (const override of overrides) {
			keys.add(
				slotKey({
					scope: override.listId == null ? 'entity' : 'list',
					audience: override.audience,
					roleId: override.roleId,
				}),
			);
		}
		return keys;
	}, [overrides]);
	const customizedListIds = useMemo(
		() =>
			new Set(
				overrides
					.map((override) => override.listId)
					.filter((id): id is string => id != null),
			),
		[overrides],
	);
	const hasEntityOverride = overrides.some(
		(override) => override.listId == null,
	);

	// ── Fetch saved searches ─────────────────────────
	const fetchSavedSearches = useCallback(async () => {
		setIsLoading(true);
		const result = await SavedSearchQueryService.list({ listKey });
		if (result.success) {
			setSavedSearches(result.data);
		}
		setIsLoading(false);
	}, [listKey]);

	useEffect(() => {
		if (isModalOpen) {
			void fetchSavedSearches();
			void fetchOverrides();
		} else {
			// Reset all state when modal closes
			setPendingChanges(new Map());
			setIsDirty(false);
			setSelectedItemId(null);
			setOverrideSlot(EVERYONE_SLOT);
			setEditorView(null);
		}
	}, [isModalOpen, fetchSavedSearches, fetchOverrides]);

	// ── Build sidebar items ──────────────────────────
	const sidebarItems = useMemo((): Array<SidebarItem> => {
		const items: Array<SidebarItem> = [];
		const seenDefaultIds = new Set<string>();

		// Entity-level tools (PD-14278): the tab strip and the preview.
		items.push(
			{
				id: TAB_STRIP_TOOL,
				name: 'Tab strip',
				type: 'tool',
				canEdit: true,
				totalUseCount: 0,
				savedSearch: null,
				defaultListConfig: null,
			},
			{
				id: PREVIEW_TOOL,
				name: 'Preview as a user',
				type: 'tool',
				canEdit: true,
				totalUseCount: 0,
				savedSearch: null,
				defaultListConfig: null,
			},
		);

		// Add default lists from editor configuration
		for (const list of editorLists) {
			seenDefaultIds.add(list.id);

			// Find matching saved search to get totalUseCount
			const matchingSavedSearch = savedSearches.find(
				(ss) => String(ss.id) === list.id && ss.type === 'default',
			);

			items.push({
				id: list.id,
				name: list.name ?? list.id,
				type: 'default',
				canEdit: false,
				totalUseCount: matchingSavedSearch?.totalUseCount ?? 0,
				savedSearch: matchingSavedSearch ?? null,
				defaultListConfig: list,
			});
		}

		// Add saved searches that are not defaults
		for (const ss of savedSearches) {
			if (ss.type === 'default' && seenDefaultIds.has(String(ss.id))) {
				continue;
			}
			if (ss.type === 'default') {
				continue;
			}
			items.push({
				id: ss.id,
				name: ss.name,
				type: ss.type,
				canEdit: ss.canEdit,
				totalUseCount: ss.totalUseCount,
				savedSearch: ss,
				defaultListConfig: null,
			});
		}

		return items;
	}, [editorLists, savedSearches]);

	// ── Auto-select initial item ─────────────────────
	useEffect(() => {
		if (selectedItemId == null && sidebarItems.length > 0 && !isLoading) {
			// Prefer initialSelectedSearchId, then activeListId, then first item
			const initialItem =
				initialSelectedSearchId != null
					? sidebarItems.find(
							(item) =>
								item.id === initialSelectedSearchId ||
								String(item.id) === String(initialSelectedSearchId),
						)
					: undefined;
			const strippedActiveId = activeListId?.startsWith('saved:')
				? activeListId.slice(6)
				: activeListId;
			const activeItem =
				initialItem ??
				sidebarItems.find(
					(item) =>
						String(item.id) === activeListId ||
						String(item.id) === strippedActiveId,
				);
			const itemToSelect =
				activeItem ?? sidebarItems.find((item) => item.type !== 'tool');
			if (itemToSelect != null) {
				selectItem(itemToSelect);
			}
		}
	}, [sidebarItems, activeListId, initialSelectedSearchId, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

	// ── Get columns for an item ──────────────────────
	const getColumnsForItem = useCallback(
		(item: SidebarItem): Array<ColumnConfiguration> => {
			// Saved search with custom columns
			if (
				item.savedSearch != null &&
				item.savedSearch.columns != null &&
				Array.isArray(item.savedSearch.columns) &&
				item.savedSearch.columns.length > 0
			) {
				return item.savedSearch.columns as Array<ColumnConfiguration>;
			}

			// Default list from config
			if (item.defaultListConfig != null) {
				return item.defaultListConfig.columns ?? [];
			}

			// Saved search without custom columns - fall back to base list
			if (item.savedSearch?.baseListId != null) {
				const baseList = editorLists.find(
					(l) => l.id === item.savedSearch?.baseListId,
				);
				if (baseList != null) {
					return baseList.columns ?? [];
				}
			}

			// Fall back to first editor list's columns
			if (editorLists.length > 0) {
				return editorLists[0]?.columns ?? [];
			}

			return [];
		},
		[editorLists],
	);

	// ── Select item ──────────────────────────────────
	const selectItem = useCallback(
		(item: SidebarItem) => {
			// Preserve current dirty state into pendingChanges before switching
			if (isDirty && selectedItemId != null) {
				setPendingChanges((prev) => {
					const next = new Map(prev);
					next.set(selectedItemId, {
						columns: workingColumns,
						defaultSortId: workingDefaultSortId,
					});
					return next;
				});
			}

			setSelectedItemId(item.id);

			// Check if we have pending changes for the target list
			const pending = pendingChanges.get(item.id);
			if (pending != null) {
				setWorkingColumns(pending.columns);
				setWorkingDefaultSortId(pending.defaultSortId);
				originalColumnsRef.current = getColumnsForItem(item);
				setIsDirty(true);
			} else {
				const cols = getColumnsForItem(item);
				setWorkingColumns(cols);
				setWorkingDefaultSortId(
					item.defaultListConfig?.defaultSortId ??
						item.savedSearch?.sortId ??
						undefined,
				);
				originalColumnsRef.current = cols;
				setIsDirty(false);
			}
		},
		[
			getColumnsForItem,
			isDirty,
			selectedItemId,
			workingColumns,
			workingDefaultSortId,
			pendingChanges,
		],
	);

	// ── Handle list updates from column editor ───────
	const handleUpdateList = useCallback(
		(updates: Partial<ListConfiguration>) => {
			const newColumns = updates.columns ?? workingColumns;
			const newSortId =
				updates.defaultSortId !== undefined
					? updates.defaultSortId
					: workingDefaultSortId;

			if (updates.columns != null) {
				setWorkingColumns(updates.columns);
				setIsDirty(true);
			}
			if (updates.defaultSortId !== undefined) {
				setWorkingDefaultSortId(updates.defaultSortId);
				setIsDirty(true);
			}

			// Keep the pending map in sync for the current item
			if (
				selectedItemId != null &&
				(updates.columns != null || updates.defaultSortId !== undefined)
			) {
				setPendingChanges((prev) => {
					const next = new Map(prev);
					next.set(selectedItemId, {
						columns: newColumns,
						defaultSortId: newSortId,
					});
					return next;
				});
			}
		},
		[selectedItemId, workingColumns, workingDefaultSortId],
	);

	// ── Set default sort from the Sort tab ───────────
	// Separate from handleUpdateList because that uses `!== undefined` to detect
	// presence, which can't express "clear the default" (set it to undefined).
	const handleSetDefaultSort = useCallback(
		(sortId: string | undefined) => {
			setWorkingDefaultSortId(sortId);
			setIsDirty(true);
			if (selectedItemId != null) {
				setPendingChanges((prev) => {
					const next = new Map(prev);
					next.set(selectedItemId, {
						columns: workingColumns,
						defaultSortId: sortId,
					});
					return next;
				});
			}
		},
		[selectedItemId, workingColumns],
	);

	// ── Selected item ────────────────────────────────
	const selectedItem = useMemo(
		() => sidebarItems.find((item) => item.id === selectedItemId) ?? null,
		[sidebarItems, selectedItemId],
	);

	// ── Editor view for a standard list + slot (PD-14278) ──
	// The modal is Workd-only, so a standard list is editable in place; what
	// is on screen is the list as it stands beneath and at the chosen slot.
	const selectedDefaultId =
		selectedItem?.type === 'default' ? String(selectedItem.id) : null;
	const currentSlotKey = slotKey(overrideSlot);
	useEffect(() => {
		if (selectedDefaultId == null) {
			setEditorView(null);
			return;
		}
		let cancelled = false;
		setIsLoadingView(true);
		void ListOverridesQueryService.editorView(listKey, {
			listId: overrideSlot.scope === 'list' ? selectedDefaultId : null,
			audience: overrideSlot.audience,
			roleId: overrideSlot.roleId,
			viaListId:
				overrideSlot.scope === 'entity' ? selectedDefaultId : undefined,
		}).then((result) => {
			if (cancelled) {
				return;
			}
			setIsLoadingView(false);
			if (!result.success || result.data == null) {
				Toast.error(t('Could not load this list for editing'));
				return;
			}
			setEditorView(result.data);
			// Seed the working state from the slot's view unless the admin
			// already has unsaved edits for this list.
			const pending = pendingChanges.get(selectedDefaultId);
			if (pending == null) {
				const cols = result.data.list.columns ?? [];
				setWorkingColumns(cols);
				setWorkingDefaultSortId(result.data.list.defaultSortId);
				originalColumnsRef.current = cols;
				setIsDirty(false);
			}
		});
		return (): void => {
			cancelled = true;
		};
		// pendingChanges is read once at load time on purpose.
	}, [listKey, selectedDefaultId, currentSlotKey]); // eslint-disable-line react-hooks/exhaustive-deps

	// ── Derive read-only state ───────────────────────
	// A non-editable saved search is read-only. Standard lists are editable in
	// place (PD-14278): the modal is Workd-only and the edit lands as an
	// override for the chosen slot.
	const isReadOnly =
		selectedItem == null ||
		selectedItem.type === 'tool' ||
		(selectedItem.type !== 'default' && !selectedItem.canEdit);
	const isDefaultSelected = selectedItem?.type === 'default';

	// ── Working list config for ColumnConfigTabs ─────
	const workingListConfig = useMemo((): ListConfiguration => {
		if (selectedItem?.defaultListConfig != null) {
			// The slot's BASE (the list beneath the slot) is what the Filters and
			// Sort tabs inherit from; the slot's own values ride in as overrides
			// so reverting to the base reads as "clear this facet".
			const base = editorView?.base ?? selectedItem.defaultListConfig;
			return {
				...base,
				id: selectedItem.defaultListConfig.id,
				columns: workingColumns,
				defaultSortId: workingDefaultSortId,
			};
		}

		// Build from saved search or fallback
		const baseList =
			editorLists.find((l) => l.id === selectedItem?.savedSearch?.baseListId) ??
			editorLists[0];

		return {
			id: String(selectedItem?.id ?? 'working'),
			name: selectedItem?.name ?? '',
			type: 'custom',
			active: true,
			columns: workingColumns,
			defaultSortId: workingDefaultSortId,
			...(baseList != null
				? {
						filterRules: baseList.filterRules,
					}
				: {}),
		};
	}, [
		selectedItem,
		workingColumns,
		workingDefaultSortId,
		editorLists,
		editorView,
	]);

	// The slot's own values, handed to the Filters / Sort tabs the way a saved
	// search's overrides are: null = inherit from the base.
	const slotOverrides = useMemo(() => {
		if (!isDefaultSelected || editorView == null) {
			return null;
		}
		const baseOverrides = listOverridesOf(editorView.base);
		const listOverrides = listOverridesOf(editorView.list);
		return {
			activeFilters: editorView.list.activeFilters ?? null,
			filterRules: editorView.list.filterRules ?? null,
			listOverrides:
				JSON.stringify(listOverrides) === JSON.stringify(baseOverrides)
					? null
					: listOverrides,
		};
	}, [isDefaultSelected, editorView]);

	// ── Base columns for the Sort tab's available pool ──
	// The full column set (including columns not currently shown) so the Sort tab
	// can offer their sorts even when the column is hidden.
	const baseColumnsForSort = useMemo<Array<ColumnConfiguration>>(() => {
		if (selectedItem?.defaultListConfig != null) {
			return selectedItem.defaultListConfig.columns ?? [];
		}
		const baseList = editorLists.find(
			(l) => l.id === selectedItem?.savedSearch?.baseListId,
		);
		return baseList?.columns ?? workingColumns;
	}, [selectedItem, editorLists, workingColumns]);

	// ── Can save directly ────────────────────────────
	const canSaveDirect =
		selectedItem != null &&
		selectedItem.type !== 'default' &&
		selectedItem.canEdit &&
		selectedItem.savedSearch != null;

	// ── Handle save all pending changes ──────────────
	const handleSaveAll = useCallback(async (): Promise<boolean> => {
		// Build the full set of changes to save, including current dirty state
		const allChanges = new Map(pendingChanges);
		if (isDirty && selectedItemId != null) {
			allChanges.set(selectedItemId, {
				columns: workingColumns,
				defaultSortId: workingDefaultSortId,
			});
		}

		let allSucceeded = true;
		let savedCount = 0;
		let overridesSaved = 0;
		const failedNames: Array<string> = [];

		// PD-14278 — a standard list's edits land as an override for the slot
		// being edited. The server diffs the arrays sent here against the layer
		// beneath the slot and stores only the delta.
		const saveOverride = async (
			item: SidebarItem,
			change: PendingChange | null,
		): Promise<boolean> => {
			const listId = String(item.id);
			const isSelected = selectedItemId != null && item.id === selectedItemId;
			const filterChanges: FiltersTabPendingChanges | null = isSelected
				? (filtersTabRef.current?.getPendingChanges() ?? null)
				: null;
			const sortChanges: SortTabPendingChanges | null = isSelected
				? (sortTabRef.current?.getPendingChanges() ?? null)
				: null;
			if (change == null && filterChanges == null && sortChanges == null) {
				return true;
			}

			const dto: SaveListOverrideDto = {
				listKey,
				listId: overrideSlot.scope === 'list' ? listId : null,
				audience: overrideSlot.audience,
				roleId: overrideSlot.audience === 'role' ? overrideSlot.roleId : null,
				...(overrideSlot.scope === 'entity' ? { viaListId: listId } : {}),
			};
			if (change != null) {
				dto.columns = change.columns;
				// Only pin the default sort when it actually differs from the layer
				// beneath; otherwise the override would freeze a value it never
				// meant to change.
				const baseSortId = editorView?.base.defaultSortId;
				if ((change.defaultSortId ?? null) !== (baseSortId ?? null)) {
					dto.defaultSortId = change.defaultSortId ?? null;
				}
			}
			if (filterChanges != null) {
				// null from the tab means "matches the base" → clear the facet.
				dto.activeFilters = filterChanges.activeFilters;
				const baseRules = editorView?.base.filterRules ?? {};
				dto.filterRules =
					filterChanges.filterRules == null
						? null
						: (diffFilterRules(baseRules, filterChanges.filterRules) as Record<
								string,
								unknown
							> | null);
			}
			if (filterChanges != null || sortChanges != null) {
				dto.listOverrides = mergeListOverrides(
					slotOverrides?.listOverrides ?? null,
					filterChanges,
					sortChanges,
				);
			}

			const result = await ListOverridesQueryService.save(dto);
			if (!result.success) {
				return false;
			}
			overridesSaved += 1;
			return true;
		};

		// Save all pending changes for saveable lists
		for (const [itemId, change] of allChanges) {
			const item = sidebarItems.find((i) => i.id === itemId);
			if (item == null) continue;

			if (item.type === 'default') {
				const ok = await saveOverride(item, change);
				if (!ok) {
					allSucceeded = false;
					failedNames.push(item.name);
				}
				continue;
			}

			// Only save editable saved searches
			const isSaveable =
				item.type !== 'tool' && item.canEdit && item.savedSearch != null;
			if (!isSaveable) continue;

			const savedSearchId = item.savedSearch?.id as number;

			// If this is the currently-selected list and the Filters / Sort tabs
			// have unsaved edits, fold them into the same update payload so columns,
			// filters, and sort options persist together (single API round-trip).
			const isSelected = selectedItemId != null && itemId === selectedItemId;
			const filterChanges: FiltersTabPendingChanges | null = isSelected
				? (filtersTabRef.current?.getPendingChanges() ?? null)
				: null;
			const sortChanges: SortTabPendingChanges | null = isSelected
				? (sortTabRef.current?.getPendingChanges() ?? null)
				: null;

			const result = await SavedSearchQueryService.update(savedSearchId, {
				columns: change.columns,
				sortId: change.defaultSortId ?? null,
				...(filterChanges != null
					? {
							activeFilters: filterChanges.activeFilters,
							filterRules: filterChanges.filterRules,
						}
					: {}),
				...(filterChanges != null || sortChanges != null
					? {
							listOverrides: mergeListOverrides(
								item.savedSearch?.listOverrides ?? null,
								filterChanges,
								sortChanges,
							),
						}
					: {}),
			});

			if (result.success) {
				savedCount++;
				// Propagate changes if this is the list currently active in the main view
				if (
					String(itemId) === activeListId ||
					`saved:${itemId}` === activeListId
				) {
					onColumnsUpdated?.(change.columns, change.defaultSortId);
				}
				// Re-apply the saved search to the live view so filter / activeFilter /
				// label-override edits show immediately (no page refresh needed).
				if (result.data != null) {
					onSavedSearchSaved?.(result.data);
				}
			} else {
				allSucceeded = false;
				failedNames.push(item.name);
			}
		}

		// Save filter / sort-only changes on the currently-selected list when
		// columns aren't dirty (the loop above only fires for items with pending
		// column changes).
		const hasColumnChange = allChanges.has(selectedItemId ?? '');
		if (!hasColumnChange && selectedItemId != null) {
			const item = sidebarItems.find((i) => i.id === selectedItemId);
			const filterChanges = filtersTabRef.current?.getPendingChanges() ?? null;
			const sortChanges = sortTabRef.current?.getPendingChanges() ?? null;
			if (
				item?.type === 'default' &&
				(filterChanges != null || sortChanges != null)
			) {
				const ok = await saveOverride(item, null);
				if (!ok) {
					allSucceeded = false;
					failedNames.push(item.name);
				}
			} else if (
				item != null &&
				item.type !== 'default' &&
				item.canEdit &&
				item.savedSearch != null &&
				(filterChanges != null || sortChanges != null)
			) {
				const savedSearchId = item.savedSearch.id as number;
				const result = await SavedSearchQueryService.update(savedSearchId, {
					...(filterChanges != null
						? {
								activeFilters: filterChanges.activeFilters,
								filterRules: filterChanges.filterRules,
							}
						: {}),
					listOverrides: mergeListOverrides(
						item.savedSearch.listOverrides ?? null,
						filterChanges,
						sortChanges,
					),
				});
				if (result.success) {
					savedCount++;
					// Re-apply to the live view so filter / sort / label-override edits
					// show immediately without a page refresh.
					if (result.data != null) {
						onSavedSearchSaved?.(result.data);
					}
				} else {
					allSucceeded = false;
					failedNames.push(item.name);
				}
			}
		}

		if (savedCount > 0) {
			Toast.show({
				text1:
					savedCount === 1
						? t('Columns saved successfully')
						: t(`${String(savedCount)} lists saved successfully`),
				type: 'success',
			});
		}
		if (overridesSaved > 0) {
			Toast.success(
				t(`Saved for ${slotLabel(overrideSlot, roles, entityLabel, t)}`),
			);
			void fetchOverrides();
			onOverridesSaved?.();
		}

		if (failedNames.length > 0) {
			Toast.error(t(`Failed to save: ${failedNames.join(', ')}`));
		}

		// Clear all pending changes and reset dirty state
		setPendingChanges(new Map());
		if (selectedItemId != null) {
			originalColumnsRef.current = workingColumns;
		}
		setIsDirty(false);
		filtersTabRef.current?.reset();
		setFiltersTabDirty(false);
		sortTabRef.current?.reset();
		setSortTabDirty(false);

		// Refresh the saved searches list
		void fetchSavedSearches();

		// Re-seed the standard list from its slot after saving so the editor
		// shows the stored state (the effect keys on the slot, so nudge it).
		if (overridesSaved > 0 && selectedDefaultId != null) {
			const view = await ListOverridesQueryService.editorView(listKey, {
				listId: overrideSlot.scope === 'list' ? selectedDefaultId : null,
				audience: overrideSlot.audience,
				roleId: overrideSlot.roleId,
				viaListId:
					overrideSlot.scope === 'entity' ? selectedDefaultId : undefined,
			});
			if (view.success && view.data != null) {
				setEditorView(view.data);
				const cols = view.data.list.columns ?? [];
				setWorkingColumns(cols);
				setWorkingDefaultSortId(view.data.list.defaultSortId);
				originalColumnsRef.current = cols;
			}
		}

		return allSucceeded;
	}, [
		isDirty,
		selectedItemId,
		selectedDefaultId,
		pendingChanges,
		workingColumns,
		workingDefaultSortId,
		sidebarItems,
		activeListId,
		listKey,
		overrideSlot,
		editorView,
		slotOverrides,
		roles,
		entityLabel,
		t,
		fetchSavedSearches,
		fetchOverrides,
		onColumnsUpdated,
		onSavedSearchSaved,
		onOverridesSaved,
	]);

	// ── Handle copy list (save as new) ──────────────
	const handleSaveAsNew = useCallback(
		(newSavedSearch: SavedSearchModel) => {
			setShowSaveAsModal(false);
			Toast.show({
				text1: t('List copied successfully'),
				type: 'success',
			});
			// Refresh and select the new item — pending changes for other lists are preserved
			void fetchSavedSearches().then(() => {
				setSelectedItemId(newSavedSearch.id);
				originalColumnsRef.current = workingColumns;
				setIsDirty(false);
				// Remove pending entry for the new list since it was just saved
				setPendingChanges((prev) => {
					const next = new Map(prev);
					next.delete(newSavedSearch.id);
					return next;
				});
			});
		},
		[fetchSavedSearches, workingColumns, t],
	);

	// ── Handle edit from sidebar ────────────────────
	const handleEditSearch = useCallback((item: SidebarItem) => {
		if (item.savedSearch != null) {
			setEditingSavedSearch(item.savedSearch);
			setShowEditModal(true);
		}
	}, []);

	const handleEditSave = useCallback(
		(_updatedSearch: SavedSearchModel) => {
			setShowEditModal(false);
			setEditingSavedSearch(null);
			void fetchSavedSearches();
		},
		[fetchSavedSearches],
	);

	const handleEditDelete = useCallback(() => {
		const deletedId = editingSavedSearch?.id;
		setShowEditModal(false);
		setEditingSavedSearch(null);
		void fetchSavedSearches().then(() => {
			// If the deleted item was selected, clear selection so auto-select picks a new one
			if (deletedId != null && selectedItemId === deletedId) {
				setSelectedItemId(null);
			}
		});
	}, [editingSavedSearch, selectedItemId, fetchSavedSearches]);

	// ── Expose imperative handle ─────────────────────
	useImperativeHandle(
		ref,
		() => ({
			saveAll: handleSaveAll,
			openCopyList: () => {
				// Capture the source list's effective overrides (saved + any unsaved
				// Filters/Sort edits) so the new copy starts with the same curation.
				const filterChanges =
					filtersTabRef.current?.getPendingChanges() ?? null;
				const sortChanges = sortTabRef.current?.getPendingChanges() ?? null;
				setCopyListOverrides(
					mergeListOverrides(
						selectedItem?.savedSearch?.listOverrides ?? null,
						filterChanges,
						sortChanges,
					) ?? undefined,
				);
				setShowSaveAsModal(true);
			},
		}),
		[handleSaveAll, selectedItem],
	);

	// ── Report state changes to parent ───────────────
	// hasPendingChanges OR's column changes with filter changes so the modal
	// footer's Apply/OK button enables when either tab has unsaved edits.
	const hasPendingChanges =
		pendingChanges.size > 0 || isDirty || filtersTabDirty || sortTabDirty;
	useEffect(() => {
		onStateChange?.({
			isDirty,
			canSaveDirect,
			hasPendingChanges,
			selectedSavedSearch: selectedItem?.savedSearch ?? null,
		});
	}, [isDirty, canSaveDirect, hasPendingChanges, selectedItem, onStateChange]);

	// ── Filter sidebar items by search ───────────────
	const filteredItems = useMemo(() => {
		if (searchQuery.length === 0) {
			return sidebarItems;
		}
		const q = searchQuery.toLowerCase();
		return sidebarItems.filter((item) => item.name.toLowerCase().includes(q));
	}, [sidebarItems, searchQuery]);

	// ── Group items by type ──────────────────────────
	const groupedItems = useMemo(() => {
		const groups: Record<string, Array<SidebarItem>> = {
			tool: [],
			default: [],
			mine: [],
			shared: [],
		};
		for (const item of filteredItems) {
			groups[item.type]?.push(item);
		}
		return groups;
	}, [filteredItems]);

	// ── Build base list ID for SaveNewListModal ──────
	const baseListId = useMemo(() => {
		if (selectedItem?.defaultListConfig != null) {
			return selectedItem.defaultListConfig.id;
		}
		if (selectedItem?.savedSearch?.baseListId != null) {
			return selectedItem.savedSearch.baseListId;
		}
		return editorLists[0]?.id ?? undefined;
	}, [selectedItem, editorLists]);

	// Derive filter state from the selected sidebar item — not from the active tab's UI state.
	// This ensures "Save As New" captures the correct filters for the list being cloned,
	// even when the user is viewing a different tab than the one they selected in the sidebar.
	const saveAsFilterState = useMemo((): Record<string, unknown> => {
		// Default lists: derive from filterRules via FilterRulesService which properly
		// unwraps defaultValue into FilterOption arrays. The savedSearch.filterState for
		// default lists stores the raw filterRules format (with defaultValue wrapper)
		// which is not the correct shape for saving a new saved search.
		if (selectedItem?.defaultListConfig != null) {
			const processed = FilterRulesService.process(
				selectedItem.defaultListConfig,
				{ hasPermission: (p) => PermissionService.can(p) },
			);
			// process() resolved the list's dynamic date defaults (e.g. "Last 90
			// Days") to concrete dates for the UI. Restore the {{DATE:...}}
			// placeholders before the copy is persisted so it keeps tracking the
			// relative range instead of freezing at copy time (PD-14159).
			return preserveDynamicDates(
				processed.filterState,
				selectedItem.defaultListConfig,
			);
		}
		// User-created saved searches: use their stored filterState directly
		if (selectedItem?.savedSearch?.filterState != null) {
			return selectedItem.savedSearch.filterState;
		}
		return activeFilterState ?? {};
	}, [selectedItem, activeFilterState]);

	// ── Render ───────────────────────────────────────
	return (
		<div className="flex h-full">
			{/* ─── Sidebar ─────────────────────────── */}
			<div
				className={clsx(
					'w-64 flex flex-col border-r',
					colors.classes.borderDefault,
					colors.classes.bgSecondary,
				)}
			>
				{/* Search */}
				<div className={clsx('p-3 border-b', colors.classes.borderDefault)}>
					<div className="relative">
						<FontAwesomeIcon
							icon={faSearch}
							className={clsx(
								'absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none',
								colors.classes.textMuted,
							)}
						/>
						<input
							type="text"
							value={searchQuery}
							onChange={(e) => {
								setSearchQuery(e.target.value);
							}}
							placeholder={t('Search lists...')}
							className={clsx(
								'w-full pl-9 pr-3 py-2 text-sm border rounded-lg',
								'has-icon-left',
								colors.classes.bgPrimary,
								colors.classes.textPrimary,
								colors.classes.borderDefault,
								'focus:outline-none focus:ring-2 focus:ring-primary-500',
								'placeholder:text-faint',
							)}
						/>
					</div>
				</div>

				{/* List items */}
				<div className="flex-1 overflow-y-auto custom-scrollbar">
					{isLoading ? (
						<div className="flex items-center justify-center p-6">
							<span className={clsx('text-sm', colors.classes.textMuted)}>
								{t('Loading...')}
							</span>
						</div>
					) : (
						<>
							{groupedItems['tool'] != null &&
								groupedItems['tool'].length > 0 && (
									<SidebarSection
										label={t(`${entityLabel} lists`)}
										items={groupedItems['tool']}
										selectedItemId={selectedItemId}
										pendingChangeIds={pendingChanges}
										onSelect={selectItem}
									/>
								)}
							{groupedItems['default'] != null &&
								groupedItems['default'].length > 0 && (
									<SidebarSection
										label={
											hasEntityOverride
												? t('Standard · customized for all')
												: t('Standard')
										}
										items={groupedItems['default']}
										selectedItemId={selectedItemId}
										pendingChangeIds={pendingChanges}
										customizedIds={customizedListIds}
										onSelect={selectItem}
										onEdit={handleEditSearch}
									/>
								)}
							{groupedItems['mine'] != null &&
								groupedItems['mine'].length > 0 && (
									<SidebarSection
										label={t('My Lists')}
										items={groupedItems['mine']}
										selectedItemId={selectedItemId}
										pendingChangeIds={pendingChanges}
										onSelect={selectItem}
										onEdit={handleEditSearch}
									/>
								)}
							{groupedItems['shared'] != null &&
								groupedItems['shared'].length > 0 && (
									<SidebarSection
										label={t('Shared')}
										items={groupedItems['shared']}
										selectedItemId={selectedItemId}
										pendingChangeIds={pendingChanges}
										onSelect={selectItem}
										onEdit={handleEditSearch}
									/>
								)}
							{filteredItems.length === 0 && (
								<div className="flex items-center justify-center p-6">
									<span className={clsx('text-sm', colors.classes.textMuted)}>
										{t('No lists found')}
									</span>
								</div>
							)}
						</>
					)}
				</div>
			</div>

			{/* ─── Column Editor ───────────────────── */}
			{/* `min-w-0` keeps the right panel inside its share of the row width.
			    As a `flex-1` child it inherits `min-width: auto` by default, which
			    resolves to the column-editor's min-content — i.e. the widest
			    non-wrappable thing inside (typically the DisplayStyleSelector's
			    custom-component pickers). Without `min-w-0` the panel grows to
			    that min-content, pushes past the modal's edge, and produces a
			    horizontal scrollbar at the bottom of the column-editor area. */}
			<div className="flex-1 min-w-0 flex flex-col">
				{selectedItem?.id === TAB_STRIP_TOOL ? (
					<TabStripEditor
						listKey={listKey}
						editorLists={editorLists}
						savedSearches={savedSearches}
						roles={roles}
						onSaved={onOverridesSaved}
					/>
				) : selectedItem?.id === PREVIEW_TOOL ? (
					<OverridePreview
						listKey={listKey}
						editorLists={editorLists}
						savedSearches={savedSearches}
					/>
				) : selectedItem != null ? (
					<>
						{/* Header bar with name and status */}
						<div
							className={clsx(
								'px-4 py-3 border-b',
								colors.classes.borderDefault,
								colors.classes.bgSecondary,
							)}
						>
							<div className="flex items-center gap-2">
								<span
									className={clsx(
										'text-sm font-semibold',
										colors.classes.textPrimary,
									)}
								>
									{t(selectedItem.name)}
								</span>
								<span
									className={clsx(
										'px-2 py-0.5 text-xs font-medium rounded',
										TYPE_BADGE_STYLES[selectedItem.type],
									)}
								>
									{t(TYPE_LABELS[selectedItem.type] ?? '')}
								</span>
								{isDirty && (
									<span className="text-xs text-warning font-medium">
										{t('(unsaved changes)')}
									</span>
								)}
							</div>
							{selectedItem.savedSearch?.description != null &&
								selectedItem.savedSearch.description.length > 0 && (
									<p
										className={clsx(
											'text-xs mt-0.5',
											colors.classes.textSecondary,
										)}
									>
										{t(selectedItem.savedSearch.description)}
									</p>
								)}
						</div>

						{/* Read-only banner for non-editable saved lists */}
						{isReadOnly && (
							<div
								className={clsx(
									'px-4 py-2 border-b text-sm',
									colors.classes.borderDefault,
									tailwindClasses.badgeWarning,
								)}
							>
								{t(
									'This is a read-only list. Use "New list from this" to make your own copy.',
								)}
							</div>
						)}

						{/* PD-14278 — a standard list is edited in place, for a slot */}
						{isDefaultSelected && (
							<div
								className={clsx(
									'px-4 py-2 border-b flex items-center gap-3 flex-wrap',
									colors.classes.borderDefault,
									colors.classes.bgPrimary,
								)}
							>
								<OverrideSlotPicker
									slot={overrideSlot}
									roles={roles}
									entityLabel={entityLabel}
									existing={existingSlotKeys}
									disabled={hasPendingChanges || isLoadingView}
									onChange={setOverrideSlot}
								/>
								{hasPendingChanges && (
									<span className={clsx('text-xs', colors.classes.textMuted)}>
										{t('Apply or cancel your changes before switching.')}
									</span>
								)}
								{isLoadingView && (
									<span className={clsx('text-xs', colors.classes.textMuted)}>
										{t('Loading...')}
									</span>
								)}
								{editorView?.exists === true && !hasPendingChanges && (
									<button
										type="button"
										className={clsx(
											'text-xs underline ml-auto',
											colors.classes.textSecondary,
										)}
										onClick={() => {
											const match = overrides.find(
												(o) =>
													(o.listId ?? null) ===
														(overrideSlot.scope === 'list'
															? selectedDefaultId
															: null) &&
													o.audience === overrideSlot.audience &&
													(o.roleId ?? null) === (overrideSlot.roleId ?? null),
											);
											if (match == null) {
												return;
											}
											void ListOverridesQueryService.remove(
												match.id,
												listKey,
											).then((result) => {
												if (result.success) {
													Toast.success(t('Customization removed'));
													void fetchOverrides();
													onOverridesSaved?.();
													setOverrideSlot({ ...overrideSlot });
												}
											});
										}}
									>
										{t('Remove this customization')}
									</button>
								)}
							</div>
						)}

						{/* Right-pane tab switcher: Columns vs Filters vs Sort */}
						<div
							className={clsx(
								'border-b',
								colors.classes.borderDefault,
								colors.classes.bgPrimary,
								'px-2',
							)}
						>
							<Tabs
								tabs={[
									{ id: 'columns', label: t('Columns'), icon: faColumns },
									{ id: 'filters', label: t('Filters'), icon: faFilter },
									{ id: 'sort', label: t('Sort'), icon: faSort },
								]}
								activeTab={activeTab}
								onTabChange={(id) => {
									setActiveTab(id as 'columns' | 'filters' | 'sort');
								}}
								variant="underline"
								showIcons
							/>
						</div>

						{/* Active tab content */}
						<div className="flex-1 overflow-hidden flex">
							{activeTab === 'columns' && (
								<ColumnConfigTabs
									listConfig={workingListConfig}
									configuration={configuration}
									onUpdateList={handleUpdateList}
									availableFields={availableFields as Array<unknown>}
									isModalOpen={isModalOpen}
									isReadOnly={isReadOnly}
								/>
							)}
							{activeTab === 'filters' && (
								<FiltersTabContent
									ref={filtersTabRef}
									listConfig={workingListConfig}
									configuration={configuration}
									savedSearchOverrides={
										isDefaultSelected
											? slotOverrides
											: selectedItem.savedSearch != null
												? {
														activeFilters:
															selectedItem.savedSearch.activeFilters ?? null,
														filterRules:
															(selectedItem.savedSearch.filterRules as Record<
																string,
																FilterRule
															> | null) ?? null,
														listOverrides:
															selectedItem.savedSearch.listOverrides ?? null,
													}
												: null
									}
									isReadOnly={isReadOnly}
									onStateChange={(state) => {
										setFiltersTabDirty(state.hasPendingChanges);
									}}
								/>
							)}
							{activeTab === 'sort' && (
								<SortTabContent
									ref={sortTabRef}
									listConfig={workingListConfig}
									configuration={configuration}
									baseColumns={baseColumnsForSort}
									savedSearchOverrides={
										isDefaultSelected
											? slotOverrides
											: selectedItem.savedSearch != null
												? {
														listOverrides:
															selectedItem.savedSearch.listOverrides ?? null,
													}
												: null
									}
									defaultSortId={workingDefaultSortId}
									onSetDefault={handleSetDefaultSort}
									isReadOnly={isReadOnly}
									onStateChange={(state) => {
										setSortTabDirty(state.hasPendingChanges);
									}}
								/>
							)}
						</div>
					</>
				) : (
					<div className="flex-1 flex items-center justify-center p-6">
						<p className={clsx('text-sm', colors.classes.textSecondary)}>
							{t('Select a list to configure columns')}
						</p>
					</div>
				)}
			</div>

			{/* ─── Save As New Modal ───────────────── */}
			<SaveNewListModal
				isOpen={showSaveAsModal}
				onClose={() => {
					setShowSaveAsModal(false);
				}}
				onSave={handleSaveAsNew}
				listKey={listKey}
				filterState={saveAsFilterState}
				sortId={workingDefaultSortId}
				sortFields={selectedItem?.savedSearch?.sortFields ?? undefined}
				columns={workingColumns}
				listOverrides={copyListOverrides}
				baseListId={baseListId}
			/>

			{/* ─── Edit Saved Search Modal ─────────── */}
			<SaveNewListModal
				isOpen={showEditModal}
				onClose={() => {
					setShowEditModal(false);
					setEditingSavedSearch(null);
				}}
				onSave={handleEditSave}
				onDelete={handleEditDelete}
				listKey={listKey}
				filterState={editingSavedSearch?.filterState ?? {}}
				sortId={editingSavedSearch?.sortId}
				sortFields={editingSavedSearch?.sortFields}
				columns={editingSavedSearch?.columns as Array<unknown> | undefined}
				baseListId={editingSavedSearch?.baseListId ?? baseListId}
				editingSavedSearch={editingSavedSearch}
			/>
		</div>
	);
});

// ── Sidebar Section ──────────────────────────────────

function SidebarSection({
	label,
	items,
	selectedItemId,
	pendingChangeIds,
	customizedIds,
	onSelect,
	onEdit,
}: {
	label: string;
	items: Array<SidebarItem>;
	selectedItemId: string | number | null;
	pendingChangeIds: Map<string | number, PendingChange>;
	/** Standard lists that carry an override for this tenant (PD-14278). */
	customizedIds?: ReadonlySet<string>;
	onSelect: (item: SidebarItem) => void;
	onEdit?: (item: SidebarItem) => void;
}): ReactElement {
	const colors = useThemeColors();
	const { t } = useTranslation();

	return (
		<div>
			<div
				className={clsx(
					'px-3 py-2 text-xs font-semibold uppercase tracking-wider',
					colors.classes.textMuted,
				)}
			>
				{label}
			</div>
			{items.map((item) => (
				<button
					key={String(item.id)}
					type="button"
					onClick={() => {
						onSelect(item);
					}}
					className={clsx(
						'w-full flex items-center gap-2 px-3 py-2 text-left',
						'transition-colors duration-150',
						item.id === selectedItemId
							? 'bg-primary-50 dark:bg-surface border-l-2 border-l-primary-500 dark:border-l-primary-400'
							: 'hover:bg-surface-hover border-l-2 border-l-transparent',
					)}
				>
					{/* Unsaved changes indicator */}
					{pendingChangeIds.has(item.id) && (
						<span
							className="h-2 w-2 rounded-full bg-warning-solid flex-shrink-0"
							title={t('Unsaved changes')}
						/>
					)}
					{item.id === TAB_STRIP_TOOL && (
						<FontAwesomeIcon
							icon={faTableColumns}
							className={clsx('h-3 w-3', colors.classes.textMuted)}
						/>
					)}
					{item.id === PREVIEW_TOOL && (
						<FontAwesomeIcon
							icon={faEye}
							className={clsx('h-3 w-3', colors.classes.textMuted)}
						/>
					)}
					<span
						className={clsx(
							'flex-1 text-sm font-medium truncate',
							colors.classes.textPrimary,
						)}
					>
						{t(item.name)}
					</span>
					{customizedIds?.has(String(item.id)) === true && (
						<span
							className={clsx(
								'text-[10px] px-1 rounded',
								tailwindClasses.badgeInfo,
							)}
							title={t('Customized for this tenant')}
						>
							{t('customized')}
						</span>
					)}
					{/* Ternary rather than `&&`: `canEdit` is typed boolean but can
					    arrive from the API as a tinyint 0/1, and a leading `0 && …`
					    renders the literal "0" in the row (PD-15581). A ternary
					    always yields the element or null. */}
					{item.canEdit && item.type !== 'default' && onEdit != null ? (
						<span
							role="button"
							tabIndex={-1}
							onClick={(e) => {
								e.stopPropagation();
								onEdit(item);
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									e.stopPropagation();
									onEdit(item);
								}
							}}
							title={t('Edit list settings')}
							className={clsx(
								'p-0.5 rounded text-faint',
								'hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30',
								'transition-colors duration-150',
							)}
						>
							<FontAwesomeIcon icon={faPencil} className="h-3 w-3" />
						</span>
					) : null}
					{item.totalUseCount > 0 && (
						<div
							className="flex items-center gap-0.5 px-1.5 py-0.5 text-xs bg-warning-solid text-white rounded"
							title={t('Popularity')}
						>
							<FontAwesomeIcon icon={faFire} className="h-2.5 w-2.5" />
							<span>{formatUseCount(item.totalUseCount)}</span>
						</div>
					)}
					{!item.canEdit && item.type !== 'default' && (
						<span
							className={clsx('text-xs', colors.classes.textMuted)}
							title={t('Read only')}
						>
							{t('RO')}
						</span>
					)}
				</button>
			))}
		</div>
	);
}
